import React, {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {assert} from 'assert-ts';
import get from 'lodash/get';
import type {
  DataSimpleValue,
  UnverifiedLinkedLiterary,
} from 'schemaDefinition/types';
import type {ManifestationV4, WorkV4} from 'services/metadata/types';
import {
  selectMetadataEditSavedMetadataSelector,
  useLazyGetManifestationExternalSuggestionsQuery,
} from 'services/metadata';
import {appendUnique} from 'services/utils/functions/appendUnique';
import {useAppSelector} from 'store/hooks';
import type {WorkMetadataSuggestions} from './types';

type ExternalSuggestionsContextType = {
  suggest: (valuePath: string) => Array<DataSimpleValue> | undefined;
  getManifestationSuggestions: (
    manifestationId: string,
  ) => Partial<ManifestationV4> | undefined;
  getWorkSuggestions: () => Partial<WorkV4> | undefined;
};

export const ExternalSuggestionsContext = createContext<
  ExternalSuggestionsContextType | undefined
>(undefined);

export const ExternalSuggestionsContextProvider: React.FC<
  PropsWithChildren
> = ({children}) => {
  const metadata = useAppSelector(selectMetadataEditSavedMetadataSelector);

  const fetched = useRef<boolean>(false);

  const [suggestions, setSuggestions] = useState<WorkMetadataSuggestions>({
    work: {},
    expressions: [],
    manifestations: [],
  });

  const [getManifestationExternalSuggestionsQuery] =
    useLazyGetManifestationExternalSuggestionsQuery();

  useEffect(() => {
    if (!fetched.current && metadata?.manifestations) {
      metadata.manifestations.forEach(m => {
        getManifestationExternalSuggestionsQuery(m)
          .unwrap()
          .then(response => {
            const series: UnverifiedLinkedLiterary[] = [];
            response.seriesTitles?.forEach((value, index) => {
              if (value) {
                series.push({
                  link: {
                    linkStatus: 'unverified',
                    name: value,
                  },
                  numberInSeries: response.seriesNumbers?.[index],
                });
              }
            });

            setSuggestions(prev => ({
              ...prev,
              work: {
                ...prev.work,
                intellectualLevel: appendUnique(
                  prev.work.intellectualLevel,
                  response.intellectualLevel,
                ),
                links: series,
              },
            }));
          });
      });
      fetched.current = true;
    }
  }, [metadata, fetched, getManifestationExternalSuggestionsQuery]);

  const suggest = useCallback(
    (valuePath: string): Array<DataSimpleValue> | undefined => {
      const suggestion = get(suggestions, valuePath);
      if (suggestion) {
        return Array.isArray(suggestion) ? suggestion : [suggestion];
      }
      return undefined;
    },
    [suggestions],
  );

  const getManifestationSuggestions = useCallback(
    (manifestationId: string) => {
      return suggestions.manifestations.find(m => m.id === manifestationId);
    },
    [suggestions.manifestations],
  );

  const getWorkSuggestions = useCallback(() => {
    return suggestions.work;
  }, [suggestions.work]);

  const value = useMemo(
    () => ({suggest, getManifestationSuggestions, getWorkSuggestions}),
    [suggest, getManifestationSuggestions, getWorkSuggestions],
  );

  return (
    <ExternalSuggestionsContext.Provider value={value}>
      {children}
    </ExternalSuggestionsContext.Provider>
  );
};

export const useExternalSuggestionsContext =
  (): ExternalSuggestionsContextType => {
    return assert(
      useContext(ExternalSuggestionsContext),
      'useExternalSuggestionsContext: context expected',
    );
  };
